import type {
  PdfPageLayer,
  PdfPageLayerViewport,
  PdfViewBox,
} from '../pdf-document-preview/types';

import { type AiProvenanceItem } from './types';

import styles from './provenance.module.scss';

type ProvenanceBoxGeometry = {
  left: number;
  top: number;
  width: number;
  height: number;
};

/**
 * Converts provenance bounding boxes (PDF points, origin carried inside the
 * bbox) into percentage geometry relative to the rendered page, so the overlay
 * scales automatically with zoom.
 *
 * Items without a complete, numeric bbox are skipped — the page still renders.
 */
function computeProvenanceBoxes(
  items: AiProvenanceItem[],
  viewport: PdfPageLayerViewport
): ProvenanceBoxGeometry[] {
  const [, y0, , y1] = viewport.viewBox;

  if (viewport.width <= 0 || viewport.height <= 0) {
    return [];
  }

  return items.flatMap((item) => {
    const bb = item.bbox;
    if (
      !bb ||
      ![bb.l, bb.t, bb.r, bb.b].every((value) => Number.isFinite(value))
    ) {
      return [];
    }

    const left = Math.min(bb.l, bb.r);
    const right = Math.max(bb.l, bb.r);
    const top = Math.min(bb.t, bb.b);
    const bottom = Math.max(bb.t, bb.b);
    const rect: PdfViewBox =
      bb.coord_origin === 'TOPLEFT'
        ? [left, y1 - (bottom - y0), right, y1 - (top - y0)]
        : [left, top, right, bottom];
    const [xA, yA, xB, yB] = viewport.convertToViewportRectangle(rect);
    const viewportLeft = Math.min(xA, xB);
    const viewportTop = Math.min(yA, yB);
    const viewportRight = Math.max(xA, xB);
    const viewportBottom = Math.max(yA, yB);

    return [
      {
        left: (viewportLeft / viewport.width) * 100,
        width: ((viewportRight - viewportLeft) / viewport.width) * 100,
        top: (viewportTop / viewport.height) * 100,
        height: ((viewportBottom - viewportTop) / viewport.height) * 100,
      },
    ];
  });
}

/** Groups provenance items by the page they belong to. */
function groupProvenanceByPage(
  items: AiProvenanceItem[] | undefined
): Map<number, AiProvenanceItem[]> {
  const grouped = new Map<number, AiProvenanceItem[]>();
  for (const item of items ?? []) {
    const list = grouped.get(item.page_no) ?? [];
    list.push(item);
    grouped.set(item.page_no, list);
  }
  return grouped;
}

export function createProvenancePageLayer(
  items: readonly AiProvenanceItem[] | undefined
): PdfPageLayer {
  const grouped = groupProvenanceByPage(items ? [...items] : undefined);
  return ({ pageNumber, pageElement, viewport }) => {
    const boxes = computeProvenanceBoxes(
      grouped.get(pageNumber) ?? [],
      viewport
    );
    if (boxes.length === 0) {
      return;
    }
    const layer = globalThis.document.createElement('div');
    layer.className = styles['layer'];
    for (const geometry of boxes) {
      const box = globalThis.document.createElement('div');
      box.className = styles['box'];
      box.style.left = `${geometry.left}%`;
      box.style.top = `${geometry.top}%`;
      box.style.width = `${geometry.width}%`;
      box.style.height = `${geometry.height}%`;
      layer.append(box);
    }
    pageElement.append(layer);
    return () => layer.remove();
  };
}
